import { articles, type ArticleMeta } from '@/mocks/articles';
import { articleContents } from '@/mocks/article-contents';

const SITE_URL = 'https://sugargoosheet.cfd';
const SITE_NAME = 'Sugargoo Sheet';
const SITE_DESC = 'The ultimate Sugargoo Spreadsheet resource hub for discovering trending products, verified finds, shopping guides, shipping information and money-saving tips.';
const FAVICON_URL = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg';

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  description: SITE_DESC,
  logo: FAVICON_URL,
  sameAs: [
    'https://kaspreadsheet.com',
  ],
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: SITE_DESC,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const articleTitles: Record<string, { headline: string; desc: string }> = {
  'sugargoo-spreadsheet-guide': {
    headline: 'The Ultimate Sugargoo Spreadsheet Guide for Beginners 2026',
    desc: 'Everything you need to know about finding, using and maximizing the best Sugargoo Spreadsheet resources for smarter shopping in 2026.',
  },
  'sugargoo-spreadsheet-review': {
    headline: 'Sugargoo Spreadsheet Review 2026 — Honest & In-Depth Analysis',
    desc: 'A complete and honest review of Sugargoo Spreadsheet resources covering product quality, organization, ease of use, update frequency and whether they are actually worth your time.',
  },
  'sugargoo-shipping-cost': {
    headline: 'Sugargoo Shipping Cost Guide 2026 — Complete Breakdown',
    desc: 'Everything you need to know about Sugargoo shipping costs — methods, estimated prices, delivery times, weight calculations and tips to minimize your international shipping fees.',
  },
  'sugargoo-coupon-guide': {
    headline: 'Sugargoo Coupon & Discount Code Guide 2026',
    desc: 'The ultimate guide to finding and using Sugargoo coupons, discount codes and promotions. Learn how to save money on every order with proven strategies and insider tips.',
  },
  'best-sugargoo-finds': {
    headline: 'Best Sugargoo Finds 2026 — Top Products Discovered This Year',
    desc: 'Discover the best Sugargoo finds of 2026 — trending sneakers, must-have streetwear pieces, hidden gem accessories and viral products that the community is loving right now.',
  },
  'is-sugargoo-legit': {
    headline: 'Is Sugargoo Legit? Complete Safety & Trust Review 2026',
    desc: 'A thorough investigation into whether Sugargoo is legitimate and safe to use. We analyze company background, security measures, user experiences and provide a comprehensive safety checklist.',
  },
  'sugargoo-alternatives': {
    headline: 'Sugargoo Alternatives 2026 — Best Shopping Agents Compared',
    desc: 'Compare Sugargoo with top alternatives like Pandabuy, CSSBuy, Superbuy and WeGoBuy. We break down pricing, shipping, features and user experience to help you choose the best agent.',
  },
  'sugargoo-shoes-guide': {
    headline: 'Sugargoo Shoes Spreadsheet Guide — Find The Best Sneakers',
    desc: 'Your complete guide to finding the best shoes and sneakers through Sugargoo Spreadsheet resources. Covers popular models, sizing tips, QC photo checks and price comparisons.',
  },
  'sugargoo-fashion-guide': {
    headline: 'Sugargoo Fashion Spreadsheet Guide — Streetwear & Style',
    desc: 'Master Sugargoo fashion shopping with our comprehensive style guide. Discover trending streetwear brands, designer-inspired pieces, seasonal essentials and styling tips from the community.',
  },
  'sugargoo-saving-tips': {
    headline: 'Sugargoo Money Saving Tips — Maximize Your Budget in 2026',
    desc: 'Proven strategies to save money when shopping through Sugargoo. Tips on combining orders, choosing the best shipping methods, finding discounts, and avoiding common money-wasting mistakes.',
  },
};

export function buildArticleSchema(slug: string): Record<string, unknown> {
  const meta: ArticleMeta | undefined = articles.find((a) => a.slug === slug);
  const data = articleTitles[slug];

  if (!meta || !data) {
    return {};
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: data.headline,
    description: data.desc,
    image: meta.heroImage,
    url: `${SITE_URL}/${slug}`,
    datePublished: '2026-01-15',
    dateModified: '2026-08-09',
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: FAVICON_URL,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/${slug}`,
    },
  };
}

export function buildFAQPageSchema(slug: string): Record<string, unknown> | null {
  const content = articleContents[slug];
  if (!content || !content.faqs || content.faqs.length === 0) {
    return null;
  }

  const mainEntity = content.faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.a.replace(/<[^>]*>/g, ''),
    },
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
}